import { v4 as uuid } from "uuid";
import {
  CartOperation,
  SyncEngineConfig,
  AddPayload,
  UpdatePayload,
  CartError,
  isAddPayload,
  isUpdatePayload,
  isRemovePayload,
} from "./types";
import { OperationQueue, getOperationQueue } from "./operation-queue";
import { classifyError } from "./error-handler";
import { CartNotifications, getNotifications } from "./notifications";
import { cartBackendService } from "@/lib/cart-backend-service";
import delay from "@/lib/delay";

const DEFAULT_CONFIG: SyncEngineConfig = {
  debounceMs: 1500,
  maxRetries: 3,
  baseBackoffMs: 1000,
  maxBackoffMs: 30000,
  flushOnNavigate: true,
};

export class CartSyncEngine {
  private queue: OperationQueue;
  private config: SyncEngineConfig;
  private notifications: CartNotifications;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private isSyncing = false;
  private syncPromise: Promise<void> | null = null;
  private channel: BroadcastChannel | null = null;
  private tabId: string;
  private initPromise: Promise<void>;
  private isInitialized = false;

  onSyncStart?: () => void;
  onSyncComplete?: (serverIdMap: Map<string, string>) => void;
  onSyncError?: (error: CartError) => void;
  onCrossTabUpdate?: () => void;

  constructor(config: Partial<SyncEngineConfig> = {}) {
    this.queue = getOperationQueue();
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.notifications = getNotifications();
    this.tabId = uuid();

    this.initPromise = this.init();
  }

  private async init(): Promise<void> {
    if (this.isInitialized) return;

    await this.queue.init();
    this.isInitialized = true;

    this.setupBroadcastChannel();

    this.queue.subscribe(() => this.scheduleSync());

    if (this.config.flushOnNavigate) {
      window.addEventListener("beforeunload", () => this.flush());
      window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          this.flush();
        }
      });
    }

    const hasPending = await this.queue.hasPending();
    if (hasPending) {
      this.scheduleSync();
    }
  }

  private setupBroadcastChannel(): void {
    try {
      this.channel = new BroadcastChannel("meucontrole-cart-sync");
      this.channel.onmessage = (event) => {
        if (event.data.tabId === this.tabId) return;

        if (
          event.data.type === "CART_UPDATED" ||
          event.data.type === "CART_CLEARED"
        ) {
          this.onCrossTabUpdate?.();
        }
      };
    } catch {
      window.addEventListener("storage", (event) => {
        if (event.key === "cart-sync-broadcast") {
          this.onCrossTabUpdate?.();
        }
      });
    }
  }

  private broadcastUpdate(): void {
    if (this.channel) {
      this.channel.postMessage({
        type: "CART_UPDATED",
        timestamp: Date.now(),
        tabId: this.tabId,
      });
    } else {
      localStorage.setItem("cart-sync-broadcast", Date.now().toString());
    }
  }

  scheduleSync(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.sync();
    }, this.config.debounceMs);
  }

  async flush(): Promise<void> {
    await this.initPromise;

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.syncPromise) {
      await this.syncPromise;
    }

    await this.sync();
  }

  async sync(): Promise<void> {
    await this.initPromise;

    if (this.isSyncing) {
      return;
    }

    const pending = await this.queue.getPending();
    if (pending.length === 0) {
      return;
    }

    this.isSyncing = true;
    this.onSyncStart?.();
    this.notifications.showSyncing();

    this.syncPromise = this.performSync(pending);

    try {
      await this.syncPromise;
    } finally {
      this.isSyncing = false;
      this.syncPromise = null;
    }
  }

  private async performSync(operations: CartOperation[]): Promise<void> {
    const operationIds = operations.map((op) => op.id);
    await this.queue.markInFlight(operationIds);

    try {
      const clearOp = operations.find((op) => op.type === "CLEAR");
      if (clearOp) {
        await this.executeClear();
        await this.queue.markCompleted(operationIds);
        this.notifications.hideSyncing();
        this.broadcastUpdate();
        this.onSyncComplete?.(new Map());
        return;
      }

      const removes = operations.filter((op) => op.type === "REMOVE");
      const updates = operations.filter((op) => op.type === "UPDATE");
      const adds = operations.filter((op) => op.type === "ADD");

      await this.executeWithRetry(() => this.executeRemoves(removes));
      await this.executeWithRetry(() => this.executeUpdates(updates));
      const serverIdMap = await this.executeWithRetry(() =>
        this.executeAdds(adds)
      );

      await this.queue.markCompleted(operationIds, serverIdMap);
      this.notifications.hideSyncing();
      this.broadcastUpdate();
      this.onSyncComplete?.(serverIdMap);
    } catch (error) {
      const cartError = classifyError(error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      await this.queue.markFailed(operationIds, errorMessage);

      if (cartError.recoverable) {
        this.scheduleRetry(operations);
      } else {
        this.notifications.showError(cartError);
        this.onSyncError?.(cartError);
      }
    }
  }

  private async executeWithRetry<T>(fn: () => Promise<T>): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < this.config.maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as Error;

        const cartError = classifyError(error);
        if (!cartError.recoverable) {
          throw error;
        }

        if (attempt >= 1) {
          this.notifications.showRetrying();
        }

        const backoff = this.calculateBackoff(attempt);
        await delay(backoff);
      }
    }

    throw lastError!;
  }

  private calculateBackoff(attempt: number): number {
    const backoff = Math.min(
      this.config.baseBackoffMs * Math.pow(2, attempt),
      this.config.maxBackoffMs
    );
    const jitter = Math.random() * 0.3 * backoff;
    return backoff + jitter;
  }

  private scheduleRetry(operations: CartOperation[]): void {
    const maxRetryCount = Math.max(...operations.map((op) => op.retryCount));

    if (maxRetryCount >= this.config.maxRetries) {
      this.notifications.showError({
        type: "UNKNOWN",
        message: "Max retries exceeded",
        recoverable: false,
        userMessage:
          "Não conseguimos salvar sua sacola. Tente de novo daqui a pouco.",
      });
      return;
    }

    const backoff = this.calculateBackoff(maxRetryCount);
    setTimeout(() => this.sync(), backoff);
  }

  private async executeRemoves(removes: CartOperation[]): Promise<void> {
    if (removes.length === 0) return;

    await Promise.all(
      removes.map((op) => {
        if (isRemovePayload(op.payload)) {
          return cartBackendService.removeCartItem(op.payload.itemId);
        }
        return Promise.resolve();
      })
    );
  }

  private async executeUpdates(updates: CartOperation[]): Promise<void> {
    if (updates.length === 0) return;

    await Promise.all(
      updates.map((op) => {
        if (isUpdatePayload(op.payload)) {
          const payload = op.payload as UpdatePayload;
          if (payload.changes.quantity !== undefined) {
            return cartBackendService.updateCartItemQuantity(
              payload.itemId,
              payload.changes.quantity
            );
          }
        }
        return Promise.resolve();
      })
    );
  }

  private async executeAdds(adds: CartOperation[]): Promise<Map<string, string>> {
    if (adds.length === 0) return new Map();

    const serverIdMap = new Map<string, string>();

    for (const op of adds) {
      if (isAddPayload(op.payload)) {
        const payload = op.payload as AddPayload;
        try {
          const response = await cartBackendService.addCartItem(payload.item);

          if (response?.id) {
            serverIdMap.set(payload.tempId, response.id);
          }
        } catch (error) {
          console.error(
            `[SyncEngine] Failed to add item ${payload.tempId}:`,
            error
          );
          throw error;
        }
      }
    }

    return serverIdMap;
  }

  private async executeClear(): Promise<void> {
    await cartBackendService.clearCart();
  }

  async getPendingCount(): Promise<number> {
    await this.initPromise;
    return this.queue.getPendingCount();
  }

  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  destroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.channel) {
      this.channel.close();
    }
  }
}

let engineInstance: CartSyncEngine | null = null;

export function getSyncEngine(
  config?: Partial<SyncEngineConfig>
): CartSyncEngine {
  if (!engineInstance) {
    engineInstance = new CartSyncEngine(config);
  }
  return engineInstance;
}

export function resetSyncEngine(): void {
  if (engineInstance) {
    engineInstance.destroy();
    engineInstance = null;
  }
}
